export type WaveformType = 'sine' | 'square' | 'sawtooth' | 'triangle' | 'pluck' | 'organ' | 'brass' | 'pad' | 'bell' | 'bass' | 'bright' | 'warm' | 'guitar';

const CUSTOM_WAVES: Record<string, { real: number[]; imag: number[] }> = {
  pluck:  { real: [0,0,0,0,0,0,0,0], imag: [0,1,0.5,0.3,0.15,0.08,0.04,0.02] },
  organ:  { real: [0,0,0,0,0,0,0,0], imag: [0,1,0,0.5,0,0.25,0,0.125] },
  brass:  { real: [0,0,0,0,0,0,0,0,0,0,0], imag: [0,0.7,0.9,1,0.8,0.5,0.3,0.2,0.15,0.1,0.05] },
  pad:    { real: [0,0,0,0,0,0], imag: [0,1,0.3,0.1,0.05,0.02] },
  bell:   { real: [0,0,0,0,0,0,0,0,0], imag: [0,1,0.6,0.4,0.8,0.3,0.1,0.5,0.2] },
  bass:   { real: [0,0,0,0,0], imag: [0,1,0.8,0.4,0.1] },
  bright: { real: [0,0,0,0,0,0,0,0,0,0,0,0,0], imag: [0,1,0.9,0.8,0.7,0.6,0.5,0.4,0.3,0.2,0.15,0.1,0.05] },
  warm:   { real: [0,0,0,0,0,0], imag: [0,1,0.5,0.15,0.04,0.01] },
  guitar: { real: [0,0.2,0.15,0.1,0.08,0.05,0.03,0.02,0.01,0.005,0,0,0,0,0,0,0], imag: [0,1,0.9,0.65,0.4,0.2,0.15,0.08,0.05,0.03,0.02,0.015,0.01,0.008,0.005,0.003,0.002] },
};

function isNativeWaveform(w: string): w is OscillatorType {
  return w === 'sine' || w === 'square' || w === 'sawtooth' || w === 'triangle';
}

interface Voice {
  oscillator: OscillatorNode;
  timbreFilter: BiquadFilterNode;
  gainNode: GainNode;
  velocityGain: number;
  coordX: number;
  coordY: number;
  octaveOffset: number;
  vibratoGainNode: GainNode;
}

export const TUNING_MARKERS: { id: string; name: string; fifth: number; description: string }[] = [
  { id: 'tet5', name: '5', fifth: 720, description: '5-TET · Indonesian slendro' },
  { id: 'tet17', name: '17', fifth: 705.88, description: '17-TET · 17 equal divisions' },
  { id: 'pythagorean', name: 'Pyth', fifth: 701.96, description: 'Pythagorean · Pure fifths (3:2)' },
  { id: 'tet12', name: '12', fifth: 700, description: '12-TET · Western standard' },
  { id: 'tet31', name: '31', fifth: 696.77, description: '31-TET · Meantone-compatible, historical significance' },
  { id: 'meantone', name: '¼MT', fifth: 696.58, description: '1/4 Meantone · Pure major thirds' },
  { id: 'tet19', name: '19', fifth: 694.74, description: '19-TET · 19 equal divisions' },
  { id: 'tet7', name: '7', fifth: 685.71, description: '7-TET · Thai, Mandinka balafon' },
];

export const FIFTH_MIN = 683;
export const FIFTH_MAX = 722;
export const FIFTH_DEFAULT = 700;

export function findNearestMarker(fifthCents: number): { marker: typeof TUNING_MARKERS[0]; distance: number } {
  let nearest = TUNING_MARKERS[0];
  let minDistance = Math.abs(fifthCents - nearest.fifth);
  
  for (const marker of TUNING_MARKERS) {
    const distance = Math.abs(fifthCents - marker.fifth);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = marker;
    }
  }
  
  return { marker: nearest, distance: minDistance };
}

export class Synth {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private eqFilter: BiquadFilterNode | null = null;
  private voices = new Map<string, Voice>();
  private sustainedVoices = new Set<string>();
  private sustain = false;
  private waveform: WaveformType = 'sawtooth';
  
  private vibratoLFO: OscillatorNode | null = null;
  private _vibratoEnabled = false;
  private vibratoRate = 5;
  private vibratoDepth = 10;

  private generator: [number, number] = [700, 1200];
  private baseFreq = 293.66;
  private _d4Hz = 293.66;

  private attackTime = 0.01;
  private releaseTime = 0.1;
  private _masterVolume = 0.3;

  private _eqValue = 0;
  
  private initSync(): void {
    if (this.context) return;

    this.context = new AudioContext({ latencyHint: 'interactive' });

    this.eqFilter = this.context.createBiquadFilter();
    this.eqFilter.type = 'highshelf';
    this.eqFilter.frequency.value = 3000;
    this.eqFilter.gain.value = 0;

    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = this._masterVolume;

    this.masterGain.connect(this.eqFilter);
    this.eqFilter.connect(this.context.destination);
  }

  tryUnlock(): void {
    this.initSync();
    if (this.context?.state === 'suspended') {
      void this.context.resume();
    }
  }

  async init(): Promise<void> {
    this.initSync();
    if (this.context?.state === 'suspended') {
      await this.context.resume();
    }
  }
  
  isInitialized(): boolean {
    return this.context !== null && this.context.state === 'running';
  }
  
  setWaveform(waveform: WaveformType): void {
    this.waveform = waveform;
    for (const voice of this.voices.values()) {
      this.applyWaveform(voice.oscillator, waveform);
    }
  }

  private applyWaveform(osc: OscillatorNode, waveform: WaveformType): void {
    if (isNativeWaveform(waveform)) {
      osc.type = waveform;
    } else {
      const spec = CUSTOM_WAVES[waveform];
      if (spec && this.context) {
        const wave = this.context.createPeriodicWave(
          new Float32Array(spec.real),
          new Float32Array(spec.imag),
        );
        osc.setPeriodicWave(wave);
      }
    }
  }
  
  getWaveform(): WaveformType {
    return this.waveform;
  }
  
  setGenerator(generator: [number, number]): void {
    this.generator = generator;
    
    this.recalculateBaseFreq();

    for (const voice of this.voices.values()) {
      const newFreq = this.getFrequency(voice.coordX, voice.coordY, voice.octaveOffset);
      voice.oscillator.frequency.value = newFreq;
    }
  }
  
  getGenerator(): [number, number] {
    return [this.generator[0], this.generator[1]];
  }
  
  setFifth(cents: number): void {
    this.setGenerator([cents, 1200]);
  }
  
  getFifth(): number {
    return this.generator[0];
  }
  
  setTuningMarker(markerId: string): void {
    const marker = TUNING_MARKERS.find(m => m.id === markerId);
    if (marker) {
      this.setFifth(marker.fifth);
    }
  }
  
  setD4Hz(hz: number): void {
    this._d4Hz = Math.max(100, Math.min(2000, hz));
    this.baseFreq = this._d4Hz;

    for (const voice of this.voices.values()) {
      const newFreq = this.getFrequency(voice.coordX, voice.coordY, voice.octaveOffset);
      voice.oscillator.frequency.value = newFreq;
    }
  }

  getD4Hz(): number {
    return this._d4Hz;
  }
  
  private recalculateBaseFreq(): void {
    this.baseFreq = this._d4Hz;
  }
  
  setMasterVolume(volume: number): void {
    this._masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain && this.context) {
      this.masterGain.gain.setTargetAtTime(
        this._masterVolume,
        this.context.currentTime,
        0.01
      );
    }
  }
  
  getMasterVolume(): number {
    return this._masterVolume;
  }
  
  setEQ(value: number): void {
    this._eqValue = Math.max(-1, Math.min(1, value));
    if (this.eqFilter && this.context) {
      const gainDb = this._eqValue * 12;
      this.eqFilter.gain.setTargetAtTime(
        gainDb,
        this.context.currentTime,
        0.01
      );
    }
  }
  
  getEQ(): number {
    return this._eqValue;
  }
  
  setSustain(enabled: boolean): void {
    this.sustain = enabled;

    if (!enabled) {
      for (const noteId of this.sustainedVoices) {
        this.stopNote(noteId, true);
      }
      this.sustainedVoices.clear();
    }
  }
  
  getSustain(): boolean {
    return this.sustain;
  }
  
  
  private ensureVibratoLFO(): void {
    if (!this.context || this.vibratoLFO) return;
    
    this.vibratoLFO = this.context.createOscillator();
    this.vibratoLFO.type = 'sine';
    this.vibratoLFO.frequency.value = this.vibratoRate;
    this.vibratoLFO.start();
  }
  
  setVibrato(enabled: boolean): void {
    if (!this.context) return;
    this._vibratoEnabled = enabled;
    const now = this.context.currentTime;
    
    if (enabled) {
      this.ensureVibratoLFO();
      for (const voice of this.voices.values()) {
        const freq = this.getFrequency(voice.coordX, voice.coordY, voice.octaveOffset);
        const depthHz = freq * (this.vibratoDepth / 1200);
        voice.vibratoGainNode.gain.setValueAtTime(depthHz, now);
        if (this.vibratoLFO) this.vibratoLFO.connect(voice.vibratoGainNode);
        voice.vibratoGainNode.connect(voice.oscillator.frequency);
      }
    } else {
      for (const voice of this.voices.values()) {
        voice.vibratoGainNode.gain.setValueAtTime(0, now);
        try { this.vibratoLFO?.disconnect(voice.vibratoGainNode); } catch { }
      }
    }
  }

  getVibrato(): boolean {
    return this._vibratoEnabled;
  }
  
  private getFrequency(x: number, y: number, octaveOffset = 0): number {
    const cents = y * this.generator[1] + x * this.generator[0] + octaveOffset * 1200;
    return this.baseFreq * Math.pow(2, cents / 1200);
  }
  
  playNote(noteId: string, x: number, y: number, octaveOffset = 0, velocity = 1): void {
    if (!this.context || !this.masterGain || this.context.state !== 'running') return;
    if (this.voices.has(noteId)) return;
    const frequency = this.getFrequency(x, y, octaveOffset);
    const oscillator = this.context.createOscillator();
    this.applyWaveform(oscillator, this.waveform);
    oscillator.frequency.value = frequency;

    const timbreFilter = this.context.createBiquadFilter();
    timbreFilter.type = 'lowpass';
    const clampedVel = Math.max(0, Math.min(1, velocity));
    timbreFilter.frequency.value = 500 * Math.pow(36, clampedVel);

    const gainNode = this.context.createGain();
    gainNode.gain.value = 0;
    oscillator.connect(timbreFilter);
    timbreFilter.connect(gainNode);
    gainNode.connect(this.masterGain);
    oscillator.start();
    gainNode.gain.setTargetAtTime(Math.max(0.01, Math.pow(clampedVel, 0.5)), this.context.currentTime, this.attackTime);

    const vibratoGainNode = this.context.createGain();
    vibratoGainNode.gain.value = 0;

    if (this._vibratoEnabled && this.vibratoLFO) {
      const depthHz = frequency * (this.vibratoDepth / 1200);
      vibratoGainNode.gain.setValueAtTime(depthHz, this.context.currentTime);
      this.vibratoLFO.connect(vibratoGainNode);
      vibratoGainNode.connect(oscillator.frequency);
    }
    const velGain = Math.max(0.01, Math.pow(clampedVel, 0.5));
    this.voices.set(noteId, {
      oscillator,
      timbreFilter,
      gainNode,
      velocityGain: velGain,
      coordX: x,
      coordY: y,
      octaveOffset,
      vibratoGainNode,
    });
  }
  
  stopNote(noteId: string, force = false): void {
    if (!this.context) return;
    
    const voice = this.voices.get(noteId);
    if (!voice) return;
    
    if (this.sustain && !force) {
      this.sustainedVoices.add(noteId);
      return;
    }

    const { gainNode, oscillator } = voice;
    const now = this.context.currentTime;

    gainNode.gain.cancelScheduledValues(now);
    gainNode.gain.setTargetAtTime(0, now, this.releaseTime);

    oscillator.stop(now + this.releaseTime * 5);

    try { this.vibratoLFO?.disconnect(voice.vibratoGainNode); } catch { }

    this.voices.delete(noteId);
    this.sustainedVoices.delete(noteId);
  }
  
  setPitchBend(noteId: string, semitones: number): void {
    if (!this.context) return;
    const voice = this.voices.get(noteId);
    if (!voice) return;
    const baseFreq = this.getFrequency(voice.coordX, voice.coordY, voice.octaveOffset);
    const bentFreq = baseFreq * Math.pow(2, semitones / 12);
    voice.oscillator.frequency.setTargetAtTime(bentFreq, this.context.currentTime, 0.005);
  }

  setTimbre(noteId: string, value: number): void {
    if (!this.context) return;
    const voice = this.voices.get(noteId);
    if (!voice) return;
    const clamped = Math.max(0, Math.min(1, value));
    const cutoff = 500 * Math.pow(36, clamped);
    voice.timbreFilter.frequency.setTargetAtTime(cutoff, this.context.currentTime, 0.01);
  }

  setPressure(noteId: string, value: number): void {
    if (!this.context) return;
    const voice = this.voices.get(noteId);
    if (!voice) return;
    const clamped = Math.max(0, Math.min(1, value));
    const gain = Math.max(0.01, clamped);
    voice.gainNode.gain.setTargetAtTime(gain, this.context.currentTime, 0.01);
  }

  stopAll(): void {
    for (const noteId of this.voices.keys()) {
      this.stopNote(noteId, true);
    }
    this.sustainedVoices.clear();
  }
  
  getActiveNotes(): string[] {
    return Array.from(this.voices.keys());
  }
  
  getVoiceCount(): number {
    return this.voices.size;
  }
  
  dispose(): void {
    this.stopAll();
    if (this.context) {
      void this.context.close();
      this.context = null;
    }
    this.masterGain = null;
    this.eqFilter = null;
  }
}
